import { useEffect, useState } from "react";
import { toast } from "sonner";
import { GripVertical, Pencil, Plus, Trash2 } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { errMsg, useApi } from "@/lib/api";

const START = "__start__";
const END = "__end__";

function EditCategory({ cat, items, onClose, onSaved }) {
  const api = useApi();
  const [name, setName] = useState("");
  const [after, setAfter] = useState(END);
  useEffect(() => {
    if (cat) {
      setName(cat.name);
      setAfter(cat.after || END);
    }
  }, [cat, items]);
  const save = async () => {
    try {
      await api.put(`/categories/${cat.id}`, { name, after: after === END ? null : after });
      toast.success("Category updated. Templates and balances now use the new name.");
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const others = items.filter((c) => cat && c.name !== cat.name);
  return (
    <Dialog open={!!cat} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="cat-edit-dialog">
        <DialogHeader><DialogTitle>Edit category</DialogTitle></DialogHeader>
        <label className="small-label" htmlFor="ce-name">Name</label>
        <Input id="ce-name" value={name} onChange={(e) => setName(e.target.value)} className="rounded-sm" data-testid="cat-edit-name" />
        <label className="small-label mt-3">Show on statements</label>
        <Select value={after} onValueChange={setAfter}>
          <SelectTrigger className="rounded-sm [&>span]:truncate" data-testid="cat-edit-position"><SelectValue /></SelectTrigger>
          <SelectContent className="max-h-80">
            <SelectItem value={START}>At the top</SelectItem>
            {others.map((c) => <SelectItem key={c.name} value={c.name}>After {c.name}</SelectItem>)}
            <SelectItem value={END}>At the end</SelectItem>
          </SelectContent>
        </Select>
        <p className="text-xs text-slate-500">Renaming updates every template and every uploaded balance, including past month ends.</p>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={name.trim().length < 2} data-testid="cat-edit-save">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Side({ title, side, items, onRemove, onEdit, admin, onMove }) {
  const [drag, setDrag] = useState(null);
  const [over, setOver] = useState(null);
  const drop = (target) => {
    if (drag && target !== drag) {
      const names = items.map((c) => c.name).filter((n) => n !== drag);
      names.splice(names.indexOf(target) + (items.findIndex((c) => c.name === drag) < items.findIndex((c) => c.name === target) ? 1 : 0), 0, drag);
      onMove(side, names);
    }
    setDrag(null);
    setOver(null);
  };
  return (
  <div className="px-5 py-4" data-testid={`cat-list-${title.toLowerCase()}`}>
    <div className="text-xs font-semibold text-slate-700 mb-2">{title}</div>
    <ul className="space-y-1 text-sm">
      {items.map((c) => (
        <li key={c.name} draggable={admin && c.custom} onDragStart={(e) => { e.dataTransfer.setData("text/plain", c.name); e.dataTransfer.effectAllowed = "move"; setDrag(c.name); }} onDragEnd={() => { setDrag(null); setOver(null); }}
          onDragOver={(e) => { if (drag) { e.preventDefault(); setOver(c.name); } }} onDrop={(e) => { e.preventDefault(); drop(c.name); }}
          className={`flex items-center justify-between gap-2 min-h-8 rounded-sm select-none px-1 -mx-1 transition-colors duration-150 ${drag === c.name ? "opacity-40" : ""} ${over === c.name && drag !== c.name ? "bg-amber-50 ring-1 ring-amber-300" : ""} ${admin && c.custom ? "cursor-grab" : ""}`}
          data-testid={`cat-row-${c.name}`}>
          <span className="truncate flex items-center gap-1">{admin && c.custom && <GripVertical className="h-4 w-4 text-slate-400 shrink-0" data-testid={`cat-drag-${c.id}`} />}{c.name}{!c.custom && <span className="ml-2 text-xs text-slate-400">standard</span>}</span>
          {admin && c.custom && (
            <span className="flex shrink-0">
              <Button variant="ghost" size="sm" onClick={() => onEdit(c, items)} data-testid={`cat-edit-${c.id}`}><Pencil className="h-4 w-4" /></Button>
              <Button variant="ghost" size="sm" onClick={() => onRemove(c)} data-testid={`cat-remove-${c.id}`}><Trash2 className="h-4 w-4" /></Button>
            </span>
          )}
        </li>
      ))}
    </ul>
  </div>
  );
}

export const CategoryManager = ({ cats, onAdd, reload }) => {
  const api = useApi();
  const [edit, setEdit] = useState(null);
  const { me } = useApp();
  const admin = me?.role === "admin" || me?.role === "owner";
  const move = async (side, names) => {
    try {
      await api.put("/categories/order", { side, names });
      toast.success("Statement order updated");
    } catch (e) {
      toast.error(errMsg(e));
    }
    reload();
  };
  const remove = async (c) => {
    if (!window.confirm(`Delete the category "${c.name}"?`)) return;
    try {
      await api.delete(`/categories/${c.id}`);
      toast.success("Category deleted");
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="panel mt-8" data-testid="cat-manager">
      <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
        <span className="small-label">AVORAlogic categories</span>
        <Button variant="outline" size="sm" className="rounded-sm" onClick={onAdd} data-testid="cat-manager-add"><Plus className="h-4 w-4 mr-1" />New category</Button>
      </div>
      <p className="px-5 pt-3 text-xs text-slate-500" data-testid="cat-manager-hint">{admin ? "Listed in statement order. Drag a custom category to move it, or use the pencil to rename it." : "Listed in statement order. Only firm admins can rename, move or delete categories."}</p>
      <div className="grid sm:grid-cols-2 sm:divide-x divide-slate-100">
        <Side title="Assets" side="asset" admin={admin} onMove={move} items={cats.assets || []} onRemove={remove} onEdit={(c, items) => setEdit({ c, items })} />
        <Side title="Liabilities" side="liability" admin={admin} onMove={move} items={cats.liabilities || []} onRemove={remove} onEdit={(c, items) => setEdit({ c, items })} />
      </div>
      <EditCategory cat={edit?.c} items={edit?.items || []} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); reload(); }} />
    </div>
  );
};
